import { useEffect, useMemo, useState } from 'react';
import { ethers } from 'ethers';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/authState';
import { formatErrorWithCode, formatMessageWithCode } from '../i18n';
import BbgDepositSuccessModal from './BbgDepositSuccessModal';
import BbgWithdrawalSuccessModal from './BbgWithdrawalSuccessModal';
import type { InGameBbgBalance } from '../lib/inGameWallet';
import type { OnChainTokenBalance } from '../lib/blockchain';
import {
  BBG_VAULT_CONTRACT_ADDRESS,
  ON_CHAIN_TOKEN_ADDRESSES,
  SHOP_ADDRESS,
  WLD_WITHDRAWAL_FEE_BASE_UNITS,
} from '../lib/blockchain';
import { supabase } from '../lib/supabaseClient';
import { useWalletTransaction } from '../lib/walletTransaction';

export type BbgTransferMode = 'deposit' | 'withdraw';

type Props = {
  mode: BbgTransferMode;
  isOpen: boolean;
  bbgBalance: InGameBbgBalance | null;
  onChainBbgBalance: OnChainTokenBalance | null;
  wldBalance: OnChainTokenBalance | null;
  isWorldApp: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
};

const WLD_FEE = 0.05;

function numericBalance(value: string | null | undefined): number {
  const parsed = Number.parseFloat((value ?? '0').replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

export default function BbgTransferModal({
  mode,
  isOpen,
  bbgBalance,
  onChainBbgBalance,
  wldBalance,
  isWorldApp,
  onClose,
  onSuccess,
}: Props) {
  const { t } = useTranslation();
  const { session, profile } = useAuth();
  const transaction = useWalletTransaction();
  const { reset: resetTransaction } = transaction;
  const [amount, setAmount] = useState('');
  const [requestId, setRequestId] = useState<string | null>(null);
  const [limits, setLimits] = useState({ min: 0.01, max: 1_000_000 });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const walletAddress = profile?.wallet_address ?? session?.user.user_metadata.wallet_address ?? null;
  const availableBbg = numericBalance(bbgBalance?.available);
  const onChainBbg = numericBalance(onChainBbgBalance?.balance);
  const wld = numericBalance(wldBalance?.balance);
  const isBusy = transaction.isLoading;
  const withdrawalSubmitted = mode === 'withdraw' && Boolean(requestId);

  const depositSuccessDetails = useMemo(() => {
    if (mode !== 'deposit' || !transaction.transactionResult) return null;

    const businessResult = transaction.transactionResult.business_result;
    const token = typeof businessResult.token === 'string' && businessResult.token.trim()
      ? businessResult.token.trim()
      : 'BBG';
    const amountBaseUnits = typeof businessResult.amount === 'string' ? businessResult.amount : null;
    let formattedAmount = amount;
    if (amountBaseUnits) {
      try {
        formattedAmount = ethers.formatUnits(amountBaseUnits, bbgBalance?.decimals ?? 18);
      } catch {
        // Keep the user-entered amount as a display fallback if an old backend response is malformed.
      }
    }

    return {
      amount: formattedAmount,
      token,
      chainId: transaction.transactionResult.chain_id,
      txHash: transaction.transactionResult.tx_hash,
      creditedAt: typeof businessResult.credited_at === 'string' ? businessResult.credited_at : null,
    };
  }, [amount, bbgBalance?.decimals, mode, transaction.transactionResult]);

  const withdrawalSuccessDetails = useMemo(() => {
    if (mode !== 'withdraw' || !transaction.transactionResult) return null;

    const businessResult = transaction.transactionResult.business_result;
    if (businessResult.withdrawal_status !== 'completed') return null;

    const withdrawalInfo = businessResult.withdrawal_info;
    const info = typeof withdrawalInfo === 'object' && withdrawalInfo !== null && !Array.isArray(withdrawalInfo)
      ? withdrawalInfo as Record<string, unknown>
      : null;
    const claimTxHash = typeof businessResult.tx_hash === 'string'
      ? businessResult.tx_hash.trim()
      : '';
    if (!claimTxHash) return null;

    return {
      amount: typeof info?.amount === 'string' && info.amount.trim() ? info.amount : amount,
      token: typeof info?.symbol === 'string' && info.symbol.trim() ? info.symbol : 'BBG',
      chainId: transaction.transactionResult.chain_id,
      txHash: claimTxHash,
    };
  }, [amount, mode, transaction.transactionResult]);

  useEffect(() => {
    if (!isOpen || mode !== 'withdraw' || !supabase) return;
    let cancelled = false;
    void supabase.schema('api').rpc('get_active_token_configs_v1').then(({ data }) => {
      if (cancelled || !Array.isArray(data)) return;
      const token = data.find((row: { symbol?: string }) => row.symbol === 'BBG') as {
        decimals?: number;
        min_withdrawal_base_units?: string | null;
        max_withdrawal_base_units?: string | null;
      } | undefined;
      if (!token) return;
      const decimals = token.decimals ?? 18;
      setLimits({
        min: token.min_withdrawal_base_units ? Number(ethers.formatUnits(token.min_withdrawal_base_units, decimals)) : 0.01,
        max: token.max_withdrawal_base_units ? Number(ethers.formatUnits(token.max_withdrawal_base_units, decimals)) : 1_000_000,
      });
    });
    return () => { cancelled = true; };
  }, [isOpen, mode]);

  useEffect(() => {
    if (!isOpen) {
      setAmount('');
      setRequestId(null);
      setErrorMessage(null);
      setSuccessMessage(null);
      resetTransaction();
    }
  }, [isOpen, resetTransaction]);

  useEffect(() => {
    if (transaction.status === 'success' && !successMessage) {
      const withdrawalStatus = typeof transaction.businessResult?.withdrawal_status === 'string'
        ? transaction.businessResult.withdrawal_status
        : null;
      setSuccessMessage(
        mode === 'withdraw' && withdrawalStatus === 'finalize_pending'
          ? t('transfer.finalizePending')
          : t('transfer.success'),
      );
      void onSuccess();
    }
    if (transaction.status === 'error' && transaction.error) {
      setErrorMessage(formatErrorWithCode(transaction.error, t, 'transfer.failed'));
    }
  }, [mode, onSuccess, successMessage, t, transaction.businessResult, transaction.error, transaction.status]);

  const validationError = useMemo(() => {
    if (!amount) return null;
    const value = Number.parseFloat(amount);
    if (!Number.isFinite(value) || value <= 0) return '請輸入有效數量。';
    if (mode === 'deposit') {
      if (value < 1) return '入金最少為 1 BBG。';
      if (value > onChainBbg) return 'World App BBG 餘額不足。';
    } else {
      if (value < limits.min) return `提現最少為 ${limits.min} BBG。`;
      if (value > limits.max) return `提現最多為 ${limits.max} BBG。`;
      if (value > availableBbg) return '遊戲內可用 BBG 餘額不足。';
      if (wld < WLD_FEE) return 'World App WLD 不足以支付提現手續費。';
    }
    return null;
  }, [amount, availableBbg, limits.max, limits.min, mode, onChainBbg, wld]);

  const submit = async () => {
    setErrorMessage(null);
    setSuccessMessage(null);
    if (!isWorldApp) {
      setErrorMessage(formatMessageWithCode(t('transfer.worldAppOnly'), t, 'WORLD_APP_REQUIRED'));
      return;
    }
    if (!walletAddress || !session?.user.id) {
      setErrorMessage(formatMessageWithCode('請先登入 Bubble Wallet。', t, 'AUTH_REQUIRED'));
      return;
    }
    if (validationError) {
      setErrorMessage(formatMessageWithCode(validationError, t, 'TRANSFER_VALIDATION_FAILED'));
      return;
    }

    try {
      const amountBaseUnits = ethers.parseUnits(amount, bbgBalance?.decimals ?? 18).toString();
      if (mode === 'withdraw') {
        if (!supabase) throw new Error('Supabase 尚未設定。');
        const { data, error } = await supabase.rpc('request_withdrawal', {
          p_user_id: session.user.id,
          p_token_symbol: 'BBG',
          p_amount_base_units: amountBaseUnits,
          p_destination_address: walletAddress,
        });
        if (error) throw error;
        const createdRequestId = (data as { request_id?: string } | null)?.request_id;
        if (!createdRequestId) throw new Error('提款申請建立失敗。');
        setRequestId(createdRequestId);
        const ok = await transaction.execute({
          tokenAddress: ON_CHAIN_TOKEN_ADDRESSES.WLD,
          recipientAddress: SHOP_ADDRESS,
          amountBaseUnits: WLD_WITHDRAWAL_FEE_BASE_UNITS,
          verification: {
            expected_token_symbol: 'WLD',
            expected_amount_base_units: WLD_WITHDRAWAL_FEE_BASE_UNITS,
            expected_to_address: SHOP_ADDRESS,
            user_wallet_address: walletAddress,
            intent: 'withdrawal_fee',
          },
          businessFunction: 'sign-withdrawal-claim',
          businessPayload: { request_id: createdRequestId },
          transactionNotice: {
            title: t('transfer.payingWldTitle'),
            description: t('transfer.payingWldDescription'),
          },
        });
        if (!ok) return;
      } else {
        await transaction.execute({
          tokenAddress: ON_CHAIN_TOKEN_ADDRESSES.BBG,
          recipientAddress: BBG_VAULT_CONTRACT_ADDRESS,
          amountBaseUnits,
          verification: {
            expected_token_symbol: 'BBG',
            expected_amount_base_units: amountBaseUnits,
            expected_to_address: BBG_VAULT_CONTRACT_ADDRESS,
            user_wallet_address: walletAddress,
            intent: 'deposit_bbg',
          },
          businessFunction: 'deposit-with-verification',
          businessPayload: { user_wallet_address: walletAddress },
          expectDepositResult: true,
          transactionNotice: {
            title: t('transfer.depositTransactionTitle'),
            description: t('transfer.depositTransactionDescription', { amount }),
          },
        });
      }
    } catch (submitError) {
      setErrorMessage(formatErrorWithCode(submitError, t, 'transfer.failed', 'TRANSFER_SUBMIT_FAILED'));
    }
  };

  if (!isOpen) return null;

  if (depositSuccessDetails) {
    return <BbgDepositSuccessModal {...depositSuccessDetails} isWorldApp={isWorldApp} onClose={onClose} />;
  }

  if (withdrawalSuccessDetails) {
    return <BbgWithdrawalSuccessModal {...withdrawalSuccessDetails} isWorldApp={isWorldApp} onClose={onClose} />;
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !isBusy) onClose();
    }}>
      <section className="transfer-modal" role="dialog" aria-modal="true" aria-labelledby="bbg-transfer-title">
        <div className="modal-header">
          <div>
            <p className="eyebrow">IN-GAME WALLET · BBG</p>
            <h2 id="bbg-transfer-title">{t(mode === 'deposit' ? 'transfer.deposit' : 'transfer.withdraw')}</h2>
          </div>
          <button className="modal-close-button" type="button" onClick={onClose} disabled={isBusy} aria-label={t('common.close')}>×</button>
        </div>

        {!isWorldApp && <p className="claim-empty">{t('transfer.worldAppOnly')}</p>}
        {mode === 'deposit' ? (
          <div className="transfer-balance-grid">
            <p>{t('transfer.onChain', { amount: onChainBbg.toLocaleString() })}</p>
            <p>{t('transfer.available', { amount: bbgBalance?.balance ?? '0' })}</p>
          </div>
        ) : (
          <div className="transfer-balance-grid">
            <p>{t('transfer.available', { amount: bbgBalance?.available ?? '0' })}</p>
            <p>{t('transfer.wldFee')}</p>
          </div>
        )}

        <label className="transfer-field">
          <span>{t('transfer.amount')}</span>
          <span className="transfer-input-wrap">
            <input value={amount} onChange={(event) => setAmount(event.target.value)} disabled={isBusy} inputMode="decimal" />
            {mode === 'deposit' && <button type="button" onClick={() => setAmount(String(onChainBbg))} disabled={isBusy}>{t('transfer.max')}</button>}
          </span>
        </label>
        {validationError && <p className="send-token-error" role="alert">{validationError}</p>}
        {errorMessage && <p className="send-token-error" role="alert">{errorMessage}</p>}
        {successMessage && <p className="transfer-success" role="status">{successMessage}</p>}
        {requestId && !successMessage && mode === 'withdraw' && <p className="transfer-pending" role="status">{t('transfer.pending')} {t('transfer.pendingNoDuplicate')}</p>}
        {transaction.isLoading && <p className="claim-stage" role="status">{t('transfer.processing')}</p>}

        <p className="transfer-hint">{t('transfer.inputHint')}</p>
        <div className="transfer-actions">
          <button className="secondary-button" type="button" onClick={onClose} disabled={isBusy}>{t('transfer.cancel')}</button>
          <button className="primary-button" type="button" onClick={() => void submit()} disabled={isBusy || withdrawalSubmitted || !amount || Boolean(validationError) || Boolean(successMessage)}>
            {isBusy ? t('transfer.processing') : t('transfer.confirm')}
          </button>
        </div>
      </section>
    </div>
  );
}
